import { useState } from "react";
import { X, Calendar, Clock, User, Search, Stethoscope, ChevronDown } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import DateCalendar from "../../DateCalendar";

interface NewTreatmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (data: any) => void;
  mode?: "booking" | "walkin";
}

// Static data
const clients = [
  { id: "1", name: "Sarah Miller", email: "sarah.miller@email.com", initials: "SM", avatarBg: "bg-purple-100 text-purple-600" },
  { id: "2", name: "James Lee", email: "james.lee@email.com", initials: "JL", avatarBg: "bg-amber-100 text-amber-600" },
  { id: "3", name: "Laura Brown", email: "laura.brown@email.com", initials: "LB", avatarBg: "bg-pink-100 text-pink-600" },
  { id: "4", name: "Emma Clark", email: "emma.clark@email.com", initials: "EC", avatarBg: "bg-blue-100 text-blue-600" },
  { id: "5", name: "Michael Davis", email: "michael.davis@email.com", initials: "MD", avatarBg: "bg-emerald-100 text-emerald-600" },
  { id: "6", name: "Rachel Adams", email: "rachel.adams@email.com", initials: "RA", avatarBg: "bg-rose-100 text-rose-600" },
];

const treatments = [
  { id: "1", name: "Hydrafacial", price: 120, duration: 60 },
  { id: "2", name: "Laser Hair Removal", price: 250, duration: 45 },
  { id: "3", name: "Chemical Peel", price: 180, duration: 30 },
  { id: "4", name: "PRP Injection", price: 350, duration: 60 },
  { id: "5", name: "Dermal Fillers", price: 280, duration: 45 },
  { id: "6", name: "Botox", price: 220, duration: 30 },
  { id: "7", name: "Microneedling", price: 200, duration: 45 },
  { id: "8", name: "Lip Filler", price: 300, duration: 30 },
  { id: "9", name: "Laser Skin Resurfacing", price: 450, duration: 90 },
];

const practitioners = [
  { id: "1", name: "Emma Taylor" },
  { id: "2", name: "Dr Saleema" },
  { id: "3", name: "Dr Harper" },
  { id: "4", name: "Sarah M." },
];

const consentOptions = [
  { value: "required", label: "Required" },
  { value: "not_required", label: "Not Required" },
  { value: "already_signed", label: "Already Signed" },
];

export default function NewTreatmentModal({
  isOpen,
  onClose,
  onSave,
  mode = "booking",
}: NewTreatmentModalProps) {
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState<typeof clients[0] | null>(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedTreatment, setSelectedTreatment] = useState("");
  const [selectedPractitioner, setSelectedPractitioner] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState("10:00");
  const [consentStatus, setConsentStatus] = useState("required");
  const [notes, setNotes] = useState("");
  const [isDateOpen, setIsDateOpen] = useState(false);

  // Filter clients based on search
  const filteredClients = clients.filter(
    (client) =>
      client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
      client.email.toLowerCase().includes(clientSearch.toLowerCase())
  );

  // Get selected treatment details
  const treatmentDetails = treatments.find((t) => t.id === selectedTreatment);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const handleClientSelect = (client: typeof clients[0]) => {
    setSelectedClient(client);
    setClientSearch(client.name);
    setShowClientDropdown(false);
  };

  const handleSave = () => {
    const treatmentData = {
      client: selectedClient,
      treatment: treatmentDetails,
      practitioner: practitioners.find((p) => p.id === selectedPractitioner),
      date: selectedDate,
      time: selectedTime,
      consentStatus,
      notes,
      status: mode === "walkin" ? "In Progress" : "Upcoming",
    };
    onSave?.(treatmentData);
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setSelectedClient(null);
    setClientSearch("");
    setSelectedTreatment("");
    setSelectedPractitioner("");
    setSelectedDate(new Date());
    setSelectedTime("10:00");
    setConsentStatus("required");
    setNotes("");
  };

  const isValid = selectedClient && selectedTreatment && selectedPractitioner;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-[#e8e0f0]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#f0ebf5]">
          <div>
            <h2 className="text-lg font-bold text-[#2d2d2d]">
              {mode === "walkin" ? "Walk-in Treatment" : "New Treatment"}
            </h2>
            <p className="text-sm text-[#6b6b6b]">
              {mode === "walkin" ? "Start a treatment for a walk-in client" : "Create a new treatment from booking"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-[#f8f5fc] rounded-lg transition-colors text-[#6b6b6b] hover:text-[#3d3d3d]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Client Selection */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Client
            </label>
            <div className="relative">
              <div className="flex items-center gap-2 px-3 py-2.5 border border-[#e8e0f0] rounded-lg focus-within:border-[#704aaf] focus-within:ring-2 focus-within:ring-[#e8e0f0] transition-all">
                <Search className="w-4 h-4 text-[#9b9b9b]" />
                <input
                  type="text"
                  value={clientSearch}
                  onChange={(e) => {
                    setClientSearch(e.target.value);
                    setShowClientDropdown(true);
                    if (!e.target.value) setSelectedClient(null);
                  }}
                  onFocus={() => setShowClientDropdown(true)}
                  placeholder="Search for a client..."
                  className="flex-1 text-sm text-[#3d3d3d] placeholder-[#9b9b9b] outline-none"
                />
              </div>

              {/* Client Dropdown */}
              {showClientDropdown && clientSearch && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#e8e0f0] rounded-lg shadow-lg max-h-48 overflow-y-auto z-10">
                  {filteredClients.length > 0 ? (
                    filteredClients.map((client) => (
                      <button
                        key={client.id}
                        onClick={() => handleClientSelect(client)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#f8f5fc] transition-colors text-left"
                      >
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold ${client.avatarBg}`}>
                          {client.initials}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#2d2d2d]">{client.name}</p>
                          <p className="text-xs text-[#6b6b6b]">{client.email}</p>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-[#6b6b6b]">
                      No clients found
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Selected Client Display */}
            {selectedClient && (
              <div className="mt-3 flex items-center gap-3 p-3 bg-[#f8f5fc] rounded-lg border border-[#e8e0f0]">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${selectedClient.avatarBg}`}>
                  {selectedClient.initials}
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#2d2d2d]">{selectedClient.name}</p>
                  <p className="text-xs text-[#6b6b6b]">{selectedClient.email}</p>
                </div>
              </div>
            )}
          </div>

          {/* Treatment Selection */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Treatment
            </label>
            <Select value={selectedTreatment} onValueChange={setSelectedTreatment}>
              <SelectTrigger className="w-full border-[#e8e0f0] h-[42px] text-sm hover:border-[#704aaf] transition-colors">
                <div className="flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-[#704aaf]" />
                  <SelectValue placeholder="Select treatment" />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-white border border-[#e8e0f0] shadow-xl rounded-xl z-[1200]">
                {treatments.map((treatment) => (
                  <SelectItem key={treatment.id} value={treatment.id}>
                    <div className="flex items-center justify-between w-full">
                      <span>{treatment.name}</span>
                      <span className="text-[#6b6b6b] ml-2">£{treatment.price}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Treatment Price Display */}
            {treatmentDetails && (
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-[#6b6b6b]">Price:</span>
                <span className="font-semibold text-[#704aaf]">£{treatmentDetails.price.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* Practitioner Selection */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Practitioner
            </label>
            <Select value={selectedPractitioner} onValueChange={setSelectedPractitioner}>
              <SelectTrigger className="w-full border-[#e8e0f0] h-[42px] text-sm hover:border-[#704aaf] transition-colors">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-[#704aaf]" />
                  <SelectValue placeholder="Select practitioner" />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-white border border-[#e8e0f0] shadow-xl rounded-xl z-[1200]">
                {practitioners.map((practitioner) => (
                  <SelectItem key={practitioner.id} value={practitioner.id}>
                    {practitioner.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date & Time */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Date & Time
            </label>
            <div className="flex gap-3">
              {/* Date Picker */}
              <Popover open={isDateOpen} onOpenChange={setIsDateOpen}>
                <PopoverTrigger asChild>
                  <button className="flex-1 flex items-center gap-2 px-3 py-2.5 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] hover:border-[#704aaf] transition-colors">
                    <Calendar className="w-4 h-4 text-[#704aaf]" />
                    <span className="truncate">{formatDate(selectedDate)}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[1200] bg-white border border-[#e8e0f0] shadow-xl rounded-xl">
                  <DateCalendar
                    selectedDate={selectedDate}
                    onSelectDate={(date) => {
                      setSelectedDate(date);
                      setIsDateOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>

              {/* Time Input */}
              <div className="flex items-center gap-2 px-3 py-2.5 border border-[#e8e0f0] rounded-lg hover:border-[#704aaf] transition-colors">
                <Clock className="w-4 h-4 text-[#704aaf]" />
                <input
                  type="time"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  className="text-sm text-[#3d3d3d] outline-none w-20"
                />
              </div>
            </div>
          </div>

          {/* Consent Status */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Consent Status
            </label>
            <Select value={consentStatus} onValueChange={setConsentStatus}>
              <SelectTrigger className="w-full border-[#e8e0f0] h-[42px] text-sm hover:border-[#704aaf] transition-colors">
                <SelectValue placeholder="Select consent status" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-[#e8e0f0] shadow-xl rounded-xl z-[1200]">
                {consentOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this treatment..."
              className="w-full px-3 py-2.5 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] placeholder-[#9b9b9b] resize-none h-20 focus:outline-none focus:ring-2 focus:ring-[#e8e0f0] focus:border-[#704aaf] transition-all"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-[#f0ebf5] bg-[#faf8fc]">
          <div className="flex items-center justify-between">
            {/* Price Summary */}
            {treatmentDetails && (
              <div className="text-sm">
                <span className="text-[#6b6b6b]">Total:</span>
                <span className="ml-2 text-lg font-bold text-[#2d2d2d]">
                  £{treatmentDetails.price.toFixed(2)}
                </span>
              </div>
            )}
            {!treatmentDetails && <div />}

            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-[#6b6b6b] hover:bg-[#f0ebf5] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!isValid}
                className="px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#764BB7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {mode === "walkin" ? "Start Treatment" : "Create Treatment"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Click outside to close client dropdown */}
      {showClientDropdown && (
        <div
          className="fixed inset-0 z-[1099]"
          onClick={() => setShowClientDropdown(false)}
        />
      )}
    </div>
  );
}


